/**
 * Name normalisation `name_norm_v1` (docs/05-MATCHING-ENGINE.md §1).
 *
 * Pure and deterministic. The lexicons (brand aliases, noise tokens, colour
 * lexicon, size-unit aliases) are injectable so the DB seed (packages/db) can
 * override the defaults without touching this code; the DB stores the
 * normalised result and the matcher compares `name_norm` values.
 */
export const DEFAULT_LEXICONS = {
    brandAliases: {
        lrp: 'la roche posay',
    },
    noiseTokens: [
        'offert',
        'gratuit',
        'pack',
        'coffret',
        'nouveaute',
        'nouveau',
        'promo',
        'happy hour',
        'white friday',
        'mapara mapromo',
        '1 achete 1 offert',
    ],
    colourTokens: [
        'blanc',
        'blanche',
        'noir',
        'noire',
        'bleu',
        'bleue',
        'rose',
        'vert',
        'verte',
        'gris',
        'grise',
        'jaune',
        'rouge',
        'violet',
        'lilas',
        'orange',
        'blue',
        'black',
        'white',
        'pink',
        'green',
        'grey',
        'gray',
        'red',
        'yellow',
        'purple',
    ],
};
const SIZE_UNIT_ALIASES = {
    mls: 'ml',
    millilitre: 'ml',
    millilitres: 'ml',
    gr: 'g',
    gramme: 'g',
    grammes: 'g',
    kgs: 'kg',
    u: 'pcs',
};
const SIZE_RE = /(\d+(?:[.,]\d+)?)\s*(ml|cl|l|kg|g|gr|mg|pieces?|pcs|u)\b/gi;
/** Extract a size from a product name → `{ value, unit }` with `ml`/`g` canonical units, else null. */
export function extractSize(name) {
    SIZE_RE.lastIndex = 0;
    let match;
    while ((match = SIZE_RE.exec(name)) !== null) {
        const rawValue = match[1];
        let unit = (match[2] ?? '').toLowerCase();
        if (rawValue === undefined || unit === '')
            continue;
        unit = SIZE_UNIT_ALIASES[unit] ?? unit;
        if (unit === 'cl') {
            return { value: Number(rawValue.replace(',', '.')) * 10, unit: 'ml' };
        }
        if (unit === 'l') {
            return { value: Number(rawValue.replace(',', '.')) * 1000, unit: 'ml' };
        }
        if (unit === 'kg') {
            return { value: Number(rawValue.replace(',', '.')) * 1000, unit: 'g' };
        }
        if (unit === 'mg') {
            return { value: Number(rawValue.replace(',', '.')) / 1000, unit: 'g' };
        }
        if (unit === 'ml') {
            return { value: Number(rawValue.replace(',', '.')), unit: 'ml' };
        }
        if (unit === 'g') {
            return { value: Number(rawValue.replace(',', '.')), unit: 'g' };
        }
        // pieces/pcs/u are not ml|g — keep scanning (docs/05 canonical units are ml/g)
    }
    return null;
}
/** Strip diacritics (NFKD), lowercase, and collapse everything the matcher should ignore. */
export function normalizeName(input, lexicons = DEFAULT_LEXICONS) {
    // º (ordinal) and ° (degree) must go BEFORE NFKD: NFKD turns º into a plain 'o',
    // which would fuse '360º' into '360o' (docs/09 shows this exact product name)
    let s = input.replace(/[°º]/g, ' ');
    s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    s = s.toLowerCase();
    s = s.replace(/["\\|_()[\]{}]/g, ' ');
    s = s.replace(/[,./;:*!?'"…–—]/g, ' ');
    // compact SPF forms: `spf 50 +` / `spf50` / `spf50+` → `spf50+` (padded with spaces;
    // extra spaces are collapsed at the end)
    s = s.replace(/\bspf\s*(\d+)\s*\+?/gi, (_m, d) => ` spf${d}+ `);
    s = s.replace(/-/g, ' ');
    // separate a unit from a preceding number so tokenisation is stable ('200ml' → '200 ml')
    s = s.replace(/\b(\d+)\s*(ml|cl|l|kg|g|gr|mg|pieces?|pcs|u)\b/gi, '$1 $2');
    // expand unit aliases before tokenising
    s = s.replace(/\b(mls|millilitres?|grammes?|gr|kgs)\b/g, (m) => SIZE_UNIT_ALIASES[m] ?? m);
    // lot/pack multiplicities are noise (`lot de 3`, `x30`, `2+1`)
    s = s.replace(/\blot de \d+\b/g, ' ');
    s = s.replace(/\b\d+\s*\+\s*\d+\b/g, ' ');
    s = s.replace(/\bx\s*\d+\b/g, ' ');
    // promotional noise
    s = s.replace(/-?\d+\s*%/g, ' ');
    for (const token of lexicons.noiseTokens) {
        s = s.split(token).join(' ');
    }
    // brand aliases (docs/05 §1 step 10); keys are post-normalisation lowercase forms
    for (const [alias, canonical] of Object.entries(lexicons.brandAliases)) {
        s = s.split(alias).join(` ${canonical} `);
    }
    s = s.replace(/\s+/g, ' ').trim();
    // drop tokens shorter than 2 chars (docs/05 §1 step 12) — but keep the single-letter
    // canonical units `g` and `l`, they carry real size signal ('50 g', '1 l')
    s = s
        .split(' ')
        .filter((t) => t.length >= 2 || t === 'g' || t === 'l')
        .join(' ');
    return s;
}
/** Detect a colour token present in a normalised name (used by the colour-conflict gate). */
export function extractColour(nameNorm, lexicons = DEFAULT_LEXICONS) {
    const tokens = nameNorm.split(' ');
    for (const token of tokens) {
        if (lexicons.colourTokens.includes(token))
            return token;
    }
    return null;
}
const TITLE_CASE_LOWERCASE_WORDS = new Set([
    'ml', 'et', 'de', 'du', 'des', 'la', 'le', 'les', 'un', 'une', 'au', 'aux', 'à', 'a', 'en',
    'dans', 'pour', 'par', 'avec', 'sans', 'sur', 'sous', 'ou', 'or', 'ni', 'mais',
    'g', 'kg', 'cl', 'l', 'd', 'l',
]);
const TITLE_CASE_ACRONYMS = new Set([
    'SPF', 'UVA', 'UVB', 'BB', 'CC', 'H2O', 'LED', 'SVR', 'ACM', 'EAN', 'TTC', 'HT', 'PVC', 'PU', 'XL', 'XS', 'XXL',
]);
function capitalizeSubToken(token, isFirstInSentence) {
    if (!token)
        return '';
    const match = token.match(/^([^a-zA-Z0-9À-ÿ]*)(.*?)([^a-zA-Z0-9À-ÿ]*)$/);
    if (!match)
        return token;
    const [, prefix, core, suffix] = match;
    if (!core)
        return token;
    const lower = core.toLowerCase();
    const upper = core.toUpperCase();
    let formattedCore = '';
    if (TITLE_CASE_ACRONYMS.has(upper)) {
        formattedCore = upper;
    }
    else if (/^SPF\d+\+?$/i.test(core)) {
        formattedCore = upper;
    }
    else if (/^\d+[a-z%]+$/i.test(core) || /^\d+x\d+[a-z%]+$/i.test(core)) {
        formattedCore = lower;
    }
    else if (/^\d+$/.test(core)) {
        formattedCore = core;
    }
    else if (!isFirstInSentence && TITLE_CASE_LOWERCASE_WORDS.has(lower)) {
        formattedCore = lower;
    }
    else {
        formattedCore = lower.charAt(0).toUpperCase() + lower.slice(1);
    }
    return `${prefix}${formattedCore}${suffix}`;
}
/**
 * Converts a product name to French Title Case (client rule 2026-10):
 * - Capitalizes every principal word (e.g. "Titania Brosse de Massage pour Cheveux")
 * - Keeps grammatical connecting words and units lowercase ("de", "pour", "à", "50ml") unless at the very start
 * - Handles hyphens ("Anti-Taches", "A-Oxitive")
 * - Handles apostrophes ("d'Eau", "L'Oréal")
 * - Preserves acronyms ("SPF50+", "H2O", "SVR")
 */
export function toTitleCase(text) {
    if (!text)
        return '';
    const trimmed = text.trim();
    if (!trimmed)
        return '';
    const words = trimmed.split(/\s+/);
    return words
        .map((word, wordIdx) => {
        if (word.includes("'")) {
            const parts = word.split("'");
            const transformedParts = parts.map((part, partIdx) => {
                if (!part)
                    return '';
                const partLower = part.toLowerCase();
                if (partIdx === 0 && TITLE_CASE_LOWERCASE_WORDS.has(partLower)) {
                    return wordIdx === 0 ? (partLower === 'd' ? 'D' : 'L') : partLower;
                }
                return capitalizeSubToken(part, false);
            });
            return transformedParts.join("'");
        }
        if (word.includes('-')) {
            const parts = word.split('-');
            const transformedParts = parts.map((part, partIdx) => {
                return capitalizeSubToken(part, wordIdx === 0 && partIdx === 0);
            });
            return transformedParts.join('-');
        }
        return capitalizeSubToken(word, wordIdx === 0);
    })
        .join(' ');
}
//# sourceMappingURL=names.js.map